import { BUILDING_DEFS } from '../content/buildings';
import { UNIT_DEFS } from '../content/units';
import { buildingSprites, unitModel } from './atlas';
import { drawBoxModel } from './boxes';

/*
 * אייקוני הקטלוג — "cameos", בשפה של RA2.
 *
 * **הם אינם נכסים, הם אותם מודלים.** ‏`render/models.ts` כבר מתאר כל יחידה וכל
 * מבנה כקופסאות, ו-`boxes.ts` כבר יודע לסובב, להצליל ולמיין פאות. אייקון הוא
 * לכן קריאה אחת למנוע שממילא רץ בשדה — ומזה נובעות שלוש תוצאות שאף ספריית
 * אייקונים לא הייתה נותנת:
 *
 *   1. **אפס קבצים להוריד.** המשחק חייב לעבוד אופליין, והוא נשאר חינמי לגמרי.
 *   2. **הזיהוי עובר.** מה שראית בקטלוג הוא בדיוק מה שייצא מהמפעל, כי זה אותו
 *      ציור. אייקון מצויר ביד היה נכון ליום שבו צויר בלבד.
 *   3. **יחידה חדשה מקבלת אייקון מעצמה**, כמו שהיא כבר מקבלת ספרייט.
 *
 * הציור נעשה פעם אחת לכל משחק ונשמר כ-`data:` URL, כלומר מכאן והלאה זה `<img>`
 * רגיל שהדפדפן מטמן — ולא קנבס לכל כרטיס בכל רינדור של React.
 */

/** צלע האייקון בתצוגה, בפיקסלים של CSS. */
export const CAMEO_PX = 52;

/** מצוירים בכפול, כדי שלא יטושטשו במסך צפוף. */
const RENDER_PX = CAMEO_PX * 2;

/**
 * זווית הפנייה של יחידה בקטלוג.
 *
 * לא אפס: יחידה שפונה ישר הצידה מראה פרופיל שטוח, והנפח — שהוא כל מה שמפריד
 * בין "טנק" ל"מלבן" — נעלם. רבע סיבוב מראה חזית וצד יחד.
 */
const CAMEO_ANGLE = Math.PI * 0.25;

/**
 * כמה מהאייקון ימלא המודל, אחרי שנמדד.
 *
 * ‏0.9 ולא 1: השוליים מונעים מקנה תותח או מלהב מסוק לגעת בדופן, וזה גם מה
 * שמשאיר מקום לתג הכמות בפינה.
 */
const CAMEO_FILL = 0.9;

/**
 * קנבס המדידה. גדול בהרבה מהמודלים (‏~128 יחידות) כדי ש**שום דבר לא ייחתך לפני
 * שנמדד** — מדידה על קנבס צר מחזירה תיבה שגויה ומקבעת את החיתוך.
 */
const MEASURE_PX = 320;

export type CameoMap = ReadonlyMap<string, string>;

/** מפתח יציב לאייקון. הפרדה בין מבנה ליחידה — שני מרחבי `type` נפרדים. */
export function cameoKey(kind: 'building' | 'unit', type: number): string {
    return `${kind === 'building' ? 'b' : 'u'}:${type}`;
}

export interface Bounds {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
}

/**
 * התיבה התוחמת של הפיקסלים שאינם שקופים ב-RGBA שטוח. `null` אם לא צויר כלום.
 *
 * מופרדת מהקנבס בכוונה: זו כל הלוגיקה שיכולה להישבר בשקט (היפוך צירים, שגיאת
 * ‎±1 בקצה, סף אלפא), והיא נבדקת ישירות בלי `getContext`.
 */
export function opaqueBoundsOf(
    data: Uint8ClampedArray | readonly number[],
    size: number,
): Bounds | null {
    let minX = size;
    let minY = size;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            // ‏8 מתוך 255: מתעלם משוליים כמעט-שקופים של החלקת הקצה.
            if ((data[(y * size + x) * 4 + 3] ?? 0) <= 8) continue;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
    }
    if (maxX < 0) return null;
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

function opaqueBounds(ctx: CanvasRenderingContext2D, size: number): Bounds | null {
    return opaqueBoundsOf(ctx.getImageData(0, 0, size, size).data, size);
}

/**
 * מצייר מודל, **מודד את מה שיצא**, ומתאים אותו לריבוע האייקון.
 *
 * **למה מדידה ולא מקדם.** קודם היה כאן מספר קבוע שהוכפל בגודל התא, ומזה נבעו
 * שתי בעיות שבר צילם: הקנה של הטנק ולהבי המסוק **גלשו מחוץ לכרטיס ונחתכו**,
 * והגדלים יצאו לא אחידים — הקוצר נראה ענק והחי"ר קטן. הסיבה היא ש"גודל התא"
 * אינו הגודל שהמודל תופס בפועל: הוא תלוי במידות הקופסאות, בגובה שלהן ובזווית
 * ההקרנה, ואי אפשר לנחש אותו ממספר אחד.
 *
 * המדידה נותנת את שתי התכונות בחינם — שום דבר לא נחתך, וכל האייקונים ממלאים
 * את אותו חלק מהריבוע. העלות היא ציור כפול, פעם אחת בעליית המשחק.
 */
function render(size: number, paint: (ctx: CanvasRenderingContext2D) => void): string | null {
    const probe = document.createElement('canvas');
    probe.width = MEASURE_PX;
    probe.height = MEASURE_PX;
    const pctx = probe.getContext('2d');
    if (!pctx) return null;
    pctx.lineJoin = 'round';
    pctx.lineCap = 'round';
    pctx.translate(MEASURE_PX / 2, MEASURE_PX / 2);
    paint(pctx);
    pctx.setTransform(1, 0, 0, 1, 0, 0);

    let box: Bounds | null = null;
    try {
        box = opaqueBounds(pctx, MEASURE_PX);
    } catch {
        // ‏`getImageData` חסום או לא ממומש — נופלים למרכוז פשוט בלי מדידה.
        box = null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;

    if (box) {
        const scale = (size * CAMEO_FILL) / Math.max(box.w, box.h);
        const w = box.w * scale;
        const h = box.h * scale;
        ctx.drawImage(probe, box.x, box.y, box.w, box.h, (size - w) / 2, (size - h) / 2, w, h);
    } else {
        ctx.drawImage(probe, 0, 0, MEASURE_PX, MEASURE_PX, 0, 0, size, size);
    }

    /*
     * ‏`toDataURL` אינו ממומש בכל סביבת טסטים, ו**קטלוג בלי אייקונים עדיף על
     * פאנל שלא עולה**: הכרטיס נופל חזרה לשם ולמחיר, שהם ממילא המידע המחייב.
     */
    try {
        return canvas.toDataURL();
    } catch {
        return null;
    }
}

/**
 * בונה את כל האייקונים בצבע השחקן.
 *
 * נקרא פעם אחת לכל משחק. הצבע נכנס כדי שהקטלוג יראה את **הצבא שלך** ולא צבע
 * ניטרלי — זה מה שהופך את האייקון למשהו שהעין מזהה אחר כך על השדה.
 */
export function buildCameos(color: string): CameoMap {
    const cameos = new Map<string, string>();

    /*
     * שני הציירים מציירים **בקואורדינטות המודל סביב הראשית** — ‏`render` כבר
     * העביר את הראשית למרכז קנבס המדידה, והוא זה שיקבע קנה מידה ומרכוז אחרי
     * שימדוד. אין כאן יותר מספרי כוונון.
     */
    UNIT_DEFS.forEach((def, type) => {
        const url = render(RENDER_PX, (ctx) => {
            drawBoxModel(ctx, unitModel(def.sprite), CAMEO_ANGLE, color);
        });
        if (url) cameos.set(cameoKey('unit', type), url);
    });

    BUILDING_DEFS.forEach((def, type) => {
        const url = render(RENDER_PX, (ctx) => {
            /*
             * גודל הציור כאן הוא **דיוק מדידה בלבד**, לא הגודל הסופי: מספיק
             * גדול כדי שהמדידה תהיה חדה, וקטן מספיק כדי שמבנה של 4×4 אריחים
             * לא יחרוג מקנבס המדידה לפני שנמדד.
             */
            const perTile = (MEASURE_PX * 0.5) / Math.max(def.w, def.h);
            buildingSprites.draw(ctx, def.sprite, 0, 0, perTile, color);
        });
        if (url) cameos.set(cameoKey('building', type), url);
    });

    return cameos;
}
